import { api, LONG_REQUEST_TIMEOUT_MS } from './client';

/**
 * Project API client
 *
 * Project object shape (from GET /projects and POST /projects):
 * {
 *   id: string,              // UUID
 *   name: string,            // Project name
 *   description: string|null,// Optional description
 *   medium: 'novel',         // Medium type
 *   is_implicit: boolean,    // True for single-story projects
 *   is_series: boolean,      // True for series, false for single story
 *   accent_color: string,    // Hex color code e.g. '#f0c866'
 *   created_at: string,      // ISO timestamp
 *   last_activity: string|null, // ISO timestamp of last session activity
 *   story_count: number,     // Number of stories in project
 *   discovery_session_id?: string  // Discovery session ID for new projects
 *   is_returning_user?: boolean     // Whether user has created projects before
 *   story_id?: string|null          // Book 1 on standalone create; null for a series
 * }
 *
 * Convert to series response (from POST /projects/:id/convert-to-series):
 * {
 *   data: {
 *     project_id: string,
 *     is_series: true,
 *     story_id: string,
 *     story_name: string
 *   }
 * }
 */
export const projectsApi = {
  /**
   * List all projects ordered by last_activity (most recent first).
   * no-store: a book created for upload must not reuse the list from
   * before that create.
   */
  list: () => api.get('/projects', { cache: 'no-store' }),

  /** Get a single project by ID */
  get: (projectId, options) => api.get(`/projects/${projectId}`, options),

  /** Get the discovery session for a project */
  getDiscovery: (projectId) => api.get(`/projects/${projectId}/discovery`),

  /**
   * Accept a Discovery suggestion: atomically create a story + session.
   * Used by Start Book 1 (series projects) and the single-book session-type
   * suggestion flow. Returns { story_id, session_id, discovery_session_id,
   * prompt_navigator }.
   * @param {string} projectId - Project UUID
   * @param {Object} data - { story_name, story_description? }
   */
  acceptSuggestion: (projectId, data) =>
    api.post(`/projects/${projectId}/discovery/accept-suggestion`, data),

  /**
   * Create a new project
   * For non-series projects, backend creates a discovery session
   * and returns discovery_session_id in the response
   */
  create: (data) => api.post('/projects', data),

  /** Update a project's name, description, or accent_color */
  update: (projectId, data) => api.patch(`/projects/${projectId}`, data),

  /** Delete a project and all associated data */
  delete: (projectId) => api.delete(`/projects/${projectId}`),

  /**
   * Export all project content as a markdown file.
   * Returns the raw Response (blob + Content-Disposition filename).
   */
  export: (projectId) => api.download(`/projects/${projectId}/export`),

  /**
   * Download a full project backup zip.
   * Returns the raw Response (blob + Content-Disposition filename).
   */
  downloadBackup: (projectId) => api.download(`/projects/${projectId}/backup`),

  /**
   * Restore a backup zip as a new project.
   * The file field name is `file`. Readable copies in the zip are ignored.
   */
  restoreBackup: (formData) => api.postForm('/projects/restore', formData, {
    timeoutMs: LONG_REQUEST_TIMEOUT_MS,
  }),

  /**
   * Convert a single-story project to a series
   * @param {string} projectId - Project UUID
   * @param {Object} options - series_name (required), book_title (optional)
   */
  convertToSeries: (projectId, { series_name, book_title }) =>
    api.post(`/projects/${projectId}/convert-to-series`, { series_name, book_title }),

  /**
   * Convert a series project back to a single-book project.
   * Flips is_series=false without deleting series-level content.
   * @param {string} projectId - Project UUID
   */
  convertToSingleBook: (projectId) =>
    api.post(`/projects/${projectId}/convert-to-single-book`),

  // Session Branching: Session tree for entire project
  /**
   * Get session tree for an entire project
   * Returns all sessions (project-level and story-level) within the project
   * @param {string} projectId - Project UUID
   * @returns {Promise<Object>} { data: { nodes: [...] } }
   */
  getSessionTree: (projectId) => api.get(`/projects/${projectId}/session-tree`)
};
